import type { Json } from "../../types/generated/public"

/**
 * Represents a ToDo that has not been persisted to the database.  These ToDos
 * are calculated based on the current app state.
 * 
 * VirtualTodos are always created in reference to a specific record, therefore they
 * always have the record_id and record_type fields.
 * 
 * Once the underlying state that caused the Todo is remedied, the VirtualTodo will no longer be created by the
 * createToDosFor* functions.  This will indicate that any associated RealizedTodo should be completed.
 */
export interface VirtualToDo {
  key: string
  title: string

  record_id: string
  record_type: RecordsThatHaveTodos
  todo_type: string

  /** Whether this TODO should show a due date, or false if we lack the ability to determine a due date. */
  has_due_date: boolean
  /**
   * The due date for this TODO.
   *  If null and hasDueDate is true, the due date is ASAP.
   */
  due_date?: string | null

  /**
   * The timestamp before which this TODO should not be visible to the user.
   * Used for delayed visibility (e.g., blob todos that should be hidden for 7 days).
   * If null or undefined, the TODO is immediately visible.
   * 
   * Please ensure you use start_of_day so TODOs don't suddenly start being visible while the user
   * is looking at their screen during the day.
   */
  not_visible_before?: string | null

  /**
   * The list of todos that this todo depends on.  This one can't be completed until all
   * of the todos in this list are completed.
   */
  depends_on?: string[]

  /**
   * A virtual TODO must always have an action.  If it did not have an action, the user could
   * mark the RealizedTodo as complete, but the underlying state that caused the todo to be created
   * may not be remedied.  Therefore, we require that all virtual todos have an action that guides
   * the user to the correct thing to do to remedy the state.
   */
  action: TodoAction

  /**
   * List of action types that can cause this todo to be marked as completed.
   * If empty, the todo will be completed when the virtual todo disappears for any reason 
   * (except delete actions).
   * If specified, the todo will only be completed when the virtual todo disappears AND
   * the triggering action's type is in this list. Otherwise, it will be deleted.
   */
  completed_with_actions?: string[]
}

export const RecordTypesThatHaveTodos = [
  'expense',
  'incident',
  'submission',
  'advance',
  'blob',
  // Not a virtual-todo record type: PlaidItem::markRequiresReauth writes a keyless
  // todo directly (see app/Models/PlaidItem.php), which is exempt from reconciliation
  // but still syncs down and still has to type-check on the client.
  'plaid_item',
] as const

export type RecordsThatHaveTodos = typeof RecordTypesThatHaveTodos[number]

export function raiseUnknownRecordType(recordType: never): never {
  throw new Error(`Record types cannot have TODOs: ${recordType}`)
}

export type TodoConfirm = string | {
  title: string,
  body?: string | null | undefined
}

/**
 * A web-style path identifying a screen -- e.g. '/expenses/3f2a.../edit'.
 *
 * Route the member to the corresponding screen in your app. The patterns are published
 * in `__fixtures__/todoRoutes/patterns.json` (and in docs/design/native-sdk-interface.md),
 * and every route the backend can emit is asserted against them by the parity tests those
 * fixtures drive.
 *
 * It stays `string` on purpose. The backend can add a pattern without the SDK shipping, so
 * **treat an unrecognized path as expected, not exceptional**: render the todo, disable its
 * affordance (or fall back to a webview), and do not throw. A union here would turn every
 * new backend todo type into a client release.
 */
export type TodoRoute = string

export type TodoAction = {
  type: 'dispatch',
  confirm?: TodoConfirm,
  action: {
    type: string,
    payload: Json
  }
} | {
  type: 'navigate',
  confirm?: TodoConfirm,
  route: TodoRoute
  state?: any
}

// Type assertions
// Ensure that TodoAction can be serialized to JSON
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _assertTodoActionSerializable: Json = {} as TodoAction

export function raiseUnknownTodoAction(action: never): never {
  throw new Error(`Unknown ToDo action ${(action as any)?.type || action}`)
}

/**
 * Coerce whatever is sitting on `todo.action` into a TodoAction we can actually run,
 * or null if it is not one.
 *
 * The type says TodoAction, but two things can put something else there at runtime:
 *
 *  - The sync endpoint briefly handed `todos.action` down as the *string* Postgres printed
 *    for the jsonb column rather than as an object (fixed in SyncReader::presentRow), and
 *    any todo that arrived during that window is still sitting in the client's persisted
 *    Redux state -- it will not be re-sent until the row changes again. Parsing the string
 *    recovers those instead of leaving them dead.
 *  - A todo written by a newer backend can name an action shape this build has never heard
 *    of. There is no recovering that one, so it comes back null and the caller degrades.
 *
 * Callers must handle null. Throwing here is what took the whole app down: this runs during
 * render, so an unrecognised action unmounted the React tree rather than spoiling one row.
 */
export function parseTodoAction(action: unknown): TodoAction | null {
  if (action === null || action === undefined) return null

  let candidate = action
  if (typeof candidate === 'string') {
    try {
      candidate = JSON.parse(candidate)
    } catch {
      return null
    }
  }

  if (typeof candidate !== 'object' || candidate === null) return null

  const { type } = candidate as { type?: unknown }
  if (type === 'dispatch') {
    const { action: dispatched } = candidate as { action?: unknown }
    if (typeof dispatched !== 'object' || dispatched === null) return null
    if (typeof (dispatched as { type?: unknown }).type !== 'string') return null
    // `payload` too, not just `type`: TodoAction declares it, and useTodoCompletion
    // feeds this object straight to `dispatch()`. Letting a payload-less action through
    // would put a reducer's `action.payload.id` one property access from a TypeError,
    // and the parser would have handed back something its own return type says it won't.
    if (!('payload' in dispatched)) return null
    return candidate as TodoAction
  }
  if (type === 'navigate') {
    if (typeof (candidate as { route?: unknown }).route !== 'string') return null
    return candidate as TodoAction
  }

  return null
}
